"use client";

import { useState, useRef, useEffect, useCallback } from "react";

/**
 * Custom hook for managing Cloudflare Turnstile widget
 * Handles widget rendering, token management, and cleanup
 *
 * @returns {Object} { turnstileRef, turnstileToken, resetTurnstile, isTurnstileReady }
 */
export function useTurnstile({ responsive = false } = {}) {
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [isTurnstileReady, setIsTurnstileReady] = useState(false);
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);

  // Render the Turnstile widget
  const renderTurnstile = useCallback(() => {
    if (!window.turnstile || !turnstileRef.current || turnstileWidgetId.current) {
      return;
    }

    try {
      // Clear container
      turnstileRef.current.innerHTML = "";
      const containerWidth = turnstileRef.current.clientWidth;
      const widgetOptions = {
        sitekey: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY,
        callback: (token) => {
          setTurnstileToken(token);
        },
        "expired-callback": () => {
          setTurnstileToken(null);
        },
        "error-callback": () => {
          setTurnstileToken(null);
          return true;
        },
        "timeout-callback": () => {
          setTurnstileToken(null);
        },
        theme: "light",
      };

      if (responsive) {
        widgetOptions.size = containerWidth > 0 && containerWidth < 300 ? "compact" : "flexible";
      }

      // Render widget
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, widgetOptions);

      setIsTurnstileReady(true);
    } catch (error) {
      console.error("Error rendering Turnstile widget:", error);
    }
  }, [responsive]);

  // Handle script load
  const handleScriptLoad = useCallback(() => {
    if (window.turnstile && turnstileRef.current) {
      renderTurnstile();
    }
  }, [renderTurnstile]);

  // Check if Turnstile is already loaded (for SPA navigation)
  // This handles cases where the script is already loaded when component mounts
  useEffect(() => {
    // Small delay to ensure DOM is ready and ref is set
    const timer = setTimeout(() => {
      if (window.turnstile && turnstileRef.current && !turnstileWidgetId.current) {
        renderTurnstile();
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [renderTurnstile]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (turnstileWidgetId.current && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetId.current);
        } catch (error) {
          console.error("Error removing Turnstile widget:", error);
        }
        turnstileWidgetId.current = null;
      }
    };
  }, []);

  // Reset the widget
  const resetTurnstile = useCallback(() => {
    setTurnstileToken(null);
    if (window.turnstile && turnstileWidgetId.current) {
      try {
        window.turnstile.reset(turnstileWidgetId.current);
      } catch (error) {
        console.error("Error resetting Turnstile widget:", error);
      }
    }
  }, []);

  return {
    turnstileRef,
    turnstileToken,
    resetTurnstile,
    isTurnstileReady,
    handleScriptLoad, // Pass this to Script onLoad
  };
}
